import { computed, getCurrentInstance, onBeforeUnmount, ref, unref } from 'vue';
import VoipDevicesAPI from 'dashboard/api/voipDevices';

const POLL_INTERVAL_MS = 3000;
const QR_TIMEOUT_MS = 120000;
const MAX_INLINE_QR_LENGTH = 700000;
const TRANSIENT_QR_ERROR_CODES = ['qr_unavailable', 'http_400', 'http_404'];

/**
 * Wizard de conexão de chamadas (provedor interno) para inbox WAHA.
 * Estados: idle | preparing | qr | connected | error
 * @param {{ inboxId: import('vue').Ref|number|string }} options
 */
export function useStackZapOnboarding({ inboxId } = {}) {
  const wizardState = ref('idle');
  const device = ref(null);
  const qrImage = ref(null);
  const providerStatus = ref(null);
  const errorMessage = ref('');
  const errorCode = ref('');
  const isBusy = ref(false);
  const technicalOpen = ref(false);
  const manualConfigOpen = ref(false);
  const bootstrapApiKey = ref('');
  const lastTestResult = ref(null);

  let pollTimeoutId = null;
  let pollActive = false;
  let pollInFlight = false;
  let pollGeneration = 0;
  let qrStartedAt = 0;
  let qrObjectUrl = null;
  /** Monotonic generation: cleanup / new fetch invalidates in-flight QR responses. */
  let qrRequestGeneration = 0;

  const resolvedInboxId = () => unref(inboxId);

  function revokeQrUrl() {
    if (qrObjectUrl) {
      URL.revokeObjectURL(qrObjectUrl);
      qrObjectUrl = null;
    }
  }

  function clearQrImage() {
    // Invalidate any in-flight QR response so it cannot assign a revoked URL.
    qrRequestGeneration += 1;
    revokeQrUrl();
    qrImage.value = null;
  }

  function applyInlineQr(payload) {
    const encoded = payload?.qr_base64;
    if (
      typeof encoded !== 'string' ||
      encoded.length === 0 ||
      encoded.length > MAX_INLINE_QR_LENGTH ||
      !/^[A-Za-z0-9+/]+={0,2}$/.test(encoded)
    ) {
      return false;
    }

    clearQrImage();
    qrImage.value = `data:image/png;base64,${encoded}`;
    wizardState.value = 'qr';
    return true;
  }

  const phoneMasked = computed(() => {
    const phone =
      providerStatus.value?.phone_number_masked ||
      providerStatus.value?.phone_number ||
      device.value?.phone_number ||
      '';
    if (!phone) return '—';
    if (phone.includes('•') || phone.includes('*')) return phone;
    if (phone.length < 6) return phone;
    return `${phone.slice(0, 4)}••••${phone.slice(-2)}`;
  });

  const inboxName = computed(
    () => device.value?.inbox?.name || providerStatus.value?.inbox_name || ''
  );

  const lastSeenAt = computed(() => {
    return (
      providerStatus.value?.last_seen_at ||
      providerStatus.value?.last_activity_at ||
      device.value?.last_seen_at ||
      null
    );
  });

  function clearPoll() {
    pollGeneration += 1;
    pollActive = false;
    pollInFlight = false;
    if (pollTimeoutId) {
      clearTimeout(pollTimeoutId);
      pollTimeoutId = null;
    }
  }

  function setError(message, code = '') {
    errorMessage.value = message || 'Não foi possível concluir a operação.';
    errorCode.value = code || '';
    wizardState.value = 'error';
    isBusy.value = false;
    clearPoll();
  }

  /** Soft QR recovery: stop preparing/poll, keep device, show recovery UI. */
  function setSoftQrRecovery(message, code) {
    errorMessage.value = message;
    errorCode.value = code;
    clearQrImage();
    clearPoll();
    wizardState.value = 'error';
    isBusy.value = false;
  }

  async function readErrorBodyAsText(data) {
    if (data == null) return '';
    if (typeof data === 'string') return data;
    // Axios responseType: 'blob' turns JSON error bodies into Blobs.
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      if (typeof data.text === 'function') return data.text();
      if (typeof data.arrayBuffer === 'function') {
        const buf = await data.arrayBuffer();
        return new TextDecoder().decode(buf);
      }
    }
    // Duck-typed blob (cross-realm / test polyfills).
    if (
      typeof data === 'object' &&
      typeof data.text === 'function' &&
      typeof data.size === 'number'
    ) {
      return data.text();
    }
    return '';
  }

  async function extractErrorData(error) {
    const raw = error?.response?.data;
    const isBlob = typeof Blob !== 'undefined' && raw instanceof Blob;
    if (
      raw &&
      typeof raw === 'object' &&
      !isBlob &&
      typeof raw.text !== 'function'
    ) {
      return raw;
    }
    try {
      const text = await readErrorBodyAsText(raw);
      if (!text) return {};
      return JSON.parse(text);
    } catch {
      return {};
    }
  }

  async function humanizeError(error, fallback) {
    const data = await extractErrorData(error);
    const code = data.error || data.error_code || '';
    const map = {
      missing_api_key:
        'Integração de chamadas ainda não liberada. Fale com o suporte.',
      invalid_api_key: 'A chave de integração é inválida. Fale com o suporte.',
      instance_not_created:
        'A conexão de chamadas não foi criada. Tente novamente.',
      qr_unavailable: 'QR Code indisponível. Gere um novo QR.',
      qr_expired: 'QR Code expirado. Gere um novo QR.',
      connecting_timeout:
        'A conexão demorou demais. Gere um novo QR ou reconecte.',
      webhook_not_registered:
        'O retorno de eventos ainda não foi registrado. Tente reconectar.',
      waha_offline:
        'O WhatsApp de mensagens está offline enquanto as chamadas estão conectadas.',
      coexistence_pending:
        'Este número ainda está em validação. Por enquanto, use um número de teste.',
      not_found: 'Conexão de chamadas não encontrada para esta caixa.',
      already_connected: 'Chamadas já estão conectadas nesta caixa.',
    };
    return {
      // Never surface raw provider messages: keep errors white-label and secret-safe.
      message: map[code] || fallback,
      code,
    };
  }

  function resolveProviderStatus(data) {
    // Contrato real: priorizar provider_status para o wizard (ex.: qr_pending).
    return (data?.provider_status || data?.status || device.value?.status || '')
      .toString()
      .toLowerCase();
  }

  function isPngBlob(blob) {
    if (!(blob instanceof Blob) || blob.size === 0) return false;
    const type = (blob.type || '').toLowerCase();
    // Strict contract: non-empty image/png only.
    return type === 'image/png';
  }

  // startPolling is assigned after apply/refresh (intentional circular poll owner).
  let startPolling = () => {};

  async function fetchQr() {
    if (!device.value?.id) return;
    qrRequestGeneration += 1;
    const generation = qrRequestGeneration;
    try {
      const response = await VoipDevicesAPI.qrCode(device.value.id);
      if (generation !== qrRequestGeneration) return;

      const blob = response?.data;
      if (!isPngBlob(blob)) {
        if (generation !== qrRequestGeneration) return;
        clearQrImage();
        setSoftQrRecovery(
          'QR Code indisponível. Gere um novo QR.',
          'qr_unavailable'
        );
        return;
      }

      if (generation !== qrRequestGeneration) return;

      revokeQrUrl();
      const objectUrl = URL.createObjectURL(blob);
      // Re-check after sync create — generation may have advanced in cleanup.
      if (generation !== qrRequestGeneration) {
        URL.revokeObjectURL(objectUrl);
        return;
      }
      qrObjectUrl = objectUrl;
      qrImage.value = objectUrl;
      wizardState.value = 'qr';
      errorMessage.value = '';
      if (!qrStartedAt) qrStartedAt = Date.now();
    } catch (error) {
      if (generation !== qrRequestGeneration) return;
      const { message, code } = await humanizeError(
        error,
        'Não foi possível carregar o QR Code.'
      );
      // The provider can report qr_pending before the PNG is readable. Keep the
      // existing status poll alive so the next tick retries instead of forcing
      // the user to restart a healthy connection attempt.
      if (TRANSIENT_QR_ERROR_CODES.includes(code)) {
        errorMessage.value = '';
        errorCode.value = '';
        wizardState.value = 'qr';
        return;
      }
      if (code === 'qr_expired') {
        setSoftQrRecovery(message, code);
      } else {
        setError(message, code);
      }
    }
  }

  /**
   * Apply provider status to wizard. Single owner of poll start.
   * @param {object|null} data
   * @param {{ fromPoll?: boolean }} options
   */
  async function applyProviderStatus(data, { fromPoll = false } = {}) {
    if (!data) return;
    providerStatus.value = data;

    // Align technical flags from exact contract fields only.
    if (typeof data.stackzap_configured === 'boolean' && device.value) {
      device.value = {
        ...device.value,
        stackzap_configured: data.stackzap_configured,
      };
    }
    if (typeof data.stackzap_webhook_configured === 'boolean' && device.value) {
      device.value = {
        ...device.value,
        stackzap_webhook_configured: data.stackzap_webhook_configured,
      };
    }

    const status = resolveProviderStatus(data);

    if (['connected', 'open'].includes(status)) {
      wizardState.value = 'connected';
      clearQrImage();
      clearPoll();
      return;
    }

    if (['qr_pending', 'qr', 'pending'].includes(status)) {
      wizardState.value = 'qr';
      // Fetch QR once when missing — never re-fetch every poll tick.
      if (!qrImage.value) {
        await fetchQr();
      }
      // Soft QR recovery moves to error — do not start/continue preparing poll.
      if (wizardState.value === 'error') return;
      if (!fromPoll) startPolling();
      return;
    }

    if (['connecting', 'preparing'].includes(status)) {
      wizardState.value = 'preparing';
      if (!fromPoll) startPolling();
      return;
    }

    if (['error', 'logged_out', 'disconnected'].includes(status)) {
      clearPoll();
      clearQrImage();
      if (status === 'disconnected' || status === 'logged_out') {
        device.value = { ...device.value, status };
        wizardState.value = 'idle';
      } else {
        errorMessage.value =
          data.message ||
          'Há um problema na conexão de chamadas. Tente reconectar.';
        errorCode.value = data.error_code || status;
        wizardState.value = 'error';
      }
      return;
    }

    if (device.value?.stackzap_configured) {
      wizardState.value =
        device.value.status === 'connected' ? 'connected' : 'idle';
    }
  }

  async function refreshProviderStatus({ fromPoll = false } = {}) {
    if (!device.value?.id) return null;
    try {
      const { data } = await VoipDevicesAPI.providerStatus(device.value.id);
      await applyProviderStatus(data, { fromPoll });
      return data;
    } catch (error) {
      // provider_status may not exist yet: fall back to device status.
      const status = (device.value?.status || '').toLowerCase();
      if (status === 'connected') {
        wizardState.value = 'connected';
        clearPoll();
      } else if (status === 'connecting') {
        wizardState.value = 'preparing';
        if (!fromPoll) startPolling();
      } else if (status === 'error') {
        setError('Conexão com erro. Tente reconectar.', 'error');
      } else {
        wizardState.value = 'idle';
      }
      return null;
    }
  }

  async function runPollTick(generation) {
    pollTimeoutId = null;
    if (generation !== pollGeneration || !pollActive || pollInFlight) return;

    if (Date.now() - qrStartedAt > QR_TIMEOUT_MS) {
      setError(
        'A conexão demorou demais. Gere um novo QR ou tente novamente.',
        'connecting_timeout'
      );
      return;
    }

    pollInFlight = true;
    try {
      // Single owner: only status refresh; QR fetched inside when needed once.
      await refreshProviderStatus({ fromPoll: true });
    } finally {
      // A reconnect, inbox switch or cleanup invalidates this in-flight tick.
      if (generation === pollGeneration) {
        pollInFlight = false;
        if (
          pollActive &&
          !['connected', 'error', 'idle'].includes(wizardState.value)
        ) {
          pollTimeoutId = setTimeout(
            () => runPollTick(generation),
            POLL_INTERVAL_MS
          );
        } else {
          pollActive = false;
        }
      }
    }
  }

  startPolling = function startPollingFn() {
    // Single owner: do not restart an active serial loop.
    if (pollActive) return;
    pollActive = true;
    pollGeneration += 1;
    const generation = pollGeneration;
    if (!qrStartedAt) qrStartedAt = Date.now();
    // First tick after interval (status already fetched by caller when needed).
    pollTimeoutId = setTimeout(() => runPollTick(generation), POLL_INTERVAL_MS);
  };

  async function loadExistingDevice() {
    clearPoll();
    clearQrImage();
    qrStartedAt = 0;
    isBusy.value = true;
    errorMessage.value = '';
    try {
      const { data } = await VoipDevicesAPI.get();
      const list = Array.isArray(data)
        ? data
        : data?.payload || data?.data || [];
      const targetId = resolvedInboxId();
      const match = list.find(
        d =>
          String(d.inbox_id) === String(targetId) ||
          String(d.inbox?.id) === String(targetId)
      );
      if (!match) {
        device.value = null;
        providerStatus.value = null;
        wizardState.value = 'idle';
        return;
      }
      device.value = match;
      await refreshProviderStatus();
    } catch (error) {
      const { message, code } = await humanizeError(
        error,
        'Não foi possível carregar o status das chamadas.'
      );
      setError(message, code);
    } finally {
      isBusy.value = false;
    }
  }

  async function startSetup() {
    clearPoll();
    clearQrImage();
    isBusy.value = true;
    errorMessage.value = '';
    errorCode.value = '';
    wizardState.value = 'preparing';
    qrStartedAt = Date.now();
    try {
      const payload = { inbox_id: resolvedInboxId() };
      // Credencial manual só se o admin abriu a seção avançada e preencheu.
      if (bootstrapApiKey.value.trim()) {
        payload.stackzap_api_key = bootstrapApiKey.value.trim();
      }
      const { data } = await VoipDevicesAPI.provision(payload);
      device.value = data.device || data;
      applyInlineQr(data);
      // Status first: connected → stop; qr_pending → QR once; preparing → poll.
      // provision already connects on server — do not call connect again.
      const statusData = await refreshProviderStatus();
      if (!statusData && device.value?.id) {
        // Fallback when provider_status fails: use provision payload status.
        await applyProviderStatus(
          {
            provider_status: resolveProviderStatus(data),
            status: data.status || data.device?.status,
            stackzap_configured:
              data.stackzap_configured ?? data.device?.stackzap_configured,
            stackzap_webhook_configured:
              data.stackzap_webhook_configured ??
              data.device?.stackzap_webhook_configured,
          },
          { fromPoll: false }
        );
      }
    } catch (error) {
      const { message, code } = await humanizeError(
        error,
        'Não foi possível iniciar a configuração de chamadas.'
      );
      if (code === 'missing_api_key') {
        manualConfigOpen.value = true;
      }
      if (code === 'already_connected') {
        // Device may already exist — load status instead of hard error only.
        try {
          await loadExistingDevice();
          if (wizardState.value === 'connected') return;
        } catch {
          /* fall through to setError */
        }
      }
      setError(message, code);
    } finally {
      isBusy.value = false;
    }
  }

  async function regenerateQr() {
    if (!device.value?.id) {
      await startSetup();
      return;
    }
    clearPoll();
    clearQrImage();
    isBusy.value = true;
    errorMessage.value = '';
    wizardState.value = 'preparing';
    qrStartedAt = Date.now();
    try {
      const { data } = await VoipDevicesAPI.reconnect(device.value.id);
      applyInlineQr(data);
      await refreshProviderStatus();
    } catch (error) {
      const { message, code } = await humanizeError(
        error,
        'Não foi possível gerar um novo QR.'
      );
      if (code === 'qr_expired' || code === 'qr_unavailable') {
        setSoftQrRecovery(message, code);
      } else {
        setError(message, code);
      }
    } finally {
      isBusy.value = false;
    }
  }

  async function reconnect() {
    if (!device.value?.id) return;
    clearPoll();
    clearQrImage();
    isBusy.value = true;
    errorMessage.value = '';
    wizardState.value = 'preparing';
    qrStartedAt = Date.now();
    try {
      const { data } = await VoipDevicesAPI.reconnect(device.value.id);
      applyInlineQr(data);
      await refreshProviderStatus();
    } catch (error) {
      const { message, code } = await humanizeError(
        error,
        'Não foi possível reconectar.'
      );
      setError(message, code);
    } finally {
      isBusy.value = false;
    }
  }

  async function disconnect() {
    if (!device.value?.id) return;
    isBusy.value = true;
    try {
      await VoipDevicesAPI.disconnect(device.value.id);
      clearPoll();
      clearQrImage();
      providerStatus.value = null;
      device.value = { ...device.value, status: 'disconnected' };
      wizardState.value = 'idle';
    } catch (error) {
      const { message, code } = await humanizeError(
        error,
        'Não foi possível desconectar.'
      );
      setError(message, code);
    } finally {
      isBusy.value = false;
    }
  }

  async function testConnection() {
    if (!device.value?.id) return;
    isBusy.value = true;
    lastTestResult.value = null;
    try {
      const { data } = await VoipDevicesAPI.testConnection(device.value.id);
      lastTestResult.value = {
        ...data,
        message:
          data?.message ||
          (data?.ok === false
            ? 'Falha ao testar a conexão.'
            : 'Conexão verificada com sucesso.'),
      };
    } catch (error) {
      const { message, code } = await humanizeError(
        error,
        'Falha ao testar a conexão.'
      );
      lastTestResult.value = { ok: false, message, error_code: code };
    } finally {
      isBusy.value = false;
    }
  }

  function cleanup() {
    clearPoll();
    clearQrImage();
    qrStartedAt = 0;
  }

  if (getCurrentInstance()) {
    onBeforeUnmount(cleanup);
  }

  return {
    wizardState,
    device,
    qrImage,
    providerStatus,
    errorMessage,
    errorCode,
    isBusy,
    technicalOpen,
    manualConfigOpen,
    bootstrapApiKey,
    lastTestResult,
    phoneMasked,
    inboxName,
    lastSeenAt,
    loadExistingDevice,
    startSetup,
    regenerateQr,
    reconnect,
    disconnect,
    testConnection,
    refreshProviderStatus,
    clearPoll,
    cleanup,
  };
}
